import { faBuilding } from "@fortawesome/pro-duotone-svg-icons"
import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { UploadOutlined, LoadingOutlined } from "@ant-design/icons"
import { Button, Form, Input, Select, Typography, Upload, message } from "antd"
import type { UploadFile } from "antd"
import { useState, useContext, useEffect } from "react"
import { TenantUpdateDTO } from "../MyTenant/tenantsModel"
import { useTenantTimeZonesQuery } from "../MyTenant/tenantsQueries"
import { useAccountSetup } from "./AccountSetupContext"
import { AuthenticationContext } from "../../AuthenticationContext"
import './AccountSetupModal.css'

const { Title, Text } = Typography
const { Dragger } = Upload

export type Props = {
  onBack: () => void
  onExit: () => void
}

const AccountSetupBusinessDetailsStep = ({ onBack, onExit }: Props) => {
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [isChangingLogo, setIsChangingLogo] = useState(false)
  const [existingLogoLoaded, setExistingLogoLoaded] = useState(false)
  const [existingLogoChecked, setExistingLogoChecked] = useState(false)

  const { state, dispatch, tenantData, saveTenantMutation, saveLogoMutation } = useAccountSetup()
  const { businessDetails } = state
  const { currentUser } = useContext(AuthenticationContext)

  // Timezone query - only needed for this step
  const timeZonesQuery = useTenantTimeZonesQuery()
  const timezones = timeZonesQuery.data?.timeZones.map(tz => ({
    value: tz.value,
    label: tz.text
  })) || []
  const timezonesLoading = timeZonesQuery.isLoading

  // Build existing logo URL
  const existingLogoUrl = currentUser?.tenant.publicFolderUrl
    ? `${currentUser.tenant.publicFolderUrl}/images/logo.png`
    : ''

  // Check if existing logo loads successfully
  useEffect(() => {
    if (existingLogoUrl && !existingLogoChecked) {
      const img = new Image()
      img.onload = () => {
        setExistingLogoLoaded(true)
        setExistingLogoChecked(true)
      }
      img.onerror = () => {
        setExistingLogoLoaded(false)
        setExistingLogoChecked(true)
      }
      img.src = existingLogoUrl
    }
  }, [existingLogoUrl, existingLogoChecked])

  // Determine if we should show the existing logo
  const showExistingLogo = existingLogoLoaded && !isChangingLogo && fileList.length === 0 && !businessDetails.logoBase64

  // Determine if we should show the uploaded logo preview
  const showUploadedLogo = businessDetails.logoBase64 && !isChangingLogo

  const handleLogoSelect = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result as string
      dispatch({ type: 'SET_LOGO', payload: base64 })

      if (tenantData) {
        const payload: TenantUpdateDTO = {
          tenant: tenantData,
          images: { logo: base64 }
        }
        saveLogoMutation.mutate(payload, {
          onSuccess: () => {
            message.success('Logo uploaded successfully')
            setIsChangingLogo(false)
          },
          onError: () => {
            message.error('Failed to upload logo')
          }
        })
      }
    }
    reader.readAsDataURL(file)
  }

  const handleFinish = async () => {
    if (!tenantData) return

    const payload: TenantUpdateDTO = {
      tenant: {
        ...tenantData,
        name: businessDetails.companyName,
        reviewWebsiteAddress: businessDetails.websiteAddress || '',
        timeZoneId: businessDetails.timezone,
        phoneNumber: businessDetails.officePhoneNumber,
      },
      images: {}
    }

    saveTenantMutation.mutate(payload)
  }

  const handleFieldChange = (field: keyof typeof businessDetails, value: string) => {
    dispatch({ type: 'UPDATE_BUSINESS_DETAILS', field, value })
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '12.5%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faBuilding} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            Tell Us About Your Business
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            We'll use this info to set up your account and personalize SynkedUP for you.
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="account-setup-form"
        >
          <Form.Item label="Company Logo">
            {showExistingLogo || showUploadedLogo ? (
              <div className="account-setup-existing-logo">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: 16,
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  background: '#fafafa'
                }}>
                  <img
                    src={showUploadedLogo ? businessDetails.logoBase64 : existingLogoUrl}
                    alt="Company logo"
                    style={{
                      maxWidth: 120,
                      maxHeight: 80,
                      objectFit: 'contain',
                      borderRadius: 4,
                      background: 'white',
                      padding: 8,
                      border: '1px solid #e8e8e8'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                      {showUploadedLogo ? 'Uploaded Logo' : 'Current Logo'}
                    </Text>
                    <Button
                      size="small"
                      onClick={() => setIsChangingLogo(true)}
                      disabled={saveLogoMutation.isLoading}
                    >
                      Change Logo
                    </Button>
                  </div>
                  {saveLogoMutation.isLoading && (
                    <LoadingOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  )}
                </div>
              </div>
            ) : (
              <>
                <Dragger
                  accept=".png,.jpg,.jpeg"
                  maxCount={1}
                  fileList={fileList}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    setFileList([file])
                    handleLogoSelect(file)
                    return false
                  }}
                  className="account-setup-logo-upload"
                  disabled={saveLogoMutation.isLoading}
                >
                  {saveLogoMutation.isLoading ? (
                    <>
                      <p className="ant-upload-drag-icon">
                        <LoadingOutlined />
                      </p>
                      <p className="ant-upload-text">Uploading...</p>
                    </>
                  ) : (
                    <>
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">Click to upload or drag and drop</p>
                      <p className="ant-upload-hint">PNG, JPG (max 5MB)</p>
                    </>
                  )}
                </Dragger>
                {isChangingLogo && (existingLogoLoaded || businessDetails.logoBase64) && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsChangingLogo(false)}
                    style={{ marginTop: 8, padding: 0 }}
                  >
                    Cancel and keep current logo
                  </Button>
                )}
              </>
            )}
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
              Upload your company logo for invoices and payments
            </Text>
          </Form.Item>

          <Form.Item
            label="Company Name"
            required
            validateStatus={!businessDetails.companyName ? 'error' : ''}
            help={!businessDetails.companyName ? 'Please enter your company name' : ''}
          >
            <Input
              placeholder="Enter your company name"
              size="large"
              value={businessDetails.companyName}
              onChange={(e) => handleFieldChange('companyName', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Website Address">
            <Input
              placeholder="https://yourcompany.com"
              size="large"
              value={businessDetails.websiteAddress}
              onChange={(e) => handleFieldChange('websiteAddress', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Timezone"
            required
            validateStatus={!businessDetails.timezone ? 'error' : ''}
            help={!businessDetails.timezone ? 'Please select your timezone' : ''}
          >
            <Select
              placeholder="Select timezone"
              size="large"
              options={timezones}
              loading={timezonesLoading}
              value={businessDetails.timezone || undefined}
              onChange={(value) => handleFieldChange('timezone', value)}
            />
          </Form.Item>

          <Form.Item
            label="Office Phone Number"
            required
            validateStatus={!businessDetails.officePhoneNumber ? 'error' : ''}
            help={!businessDetails.officePhoneNumber ? 'Please enter your office phone number' : ''}
          >
            <Input
              placeholder="(555) 555-5555"
              size="large"
              value={businessDetails.officePhoneNumber}
              onChange={(e) => handleFieldChange('officePhoneNumber', e.target.value)}
            />
          </Form.Item>

          <div className="account-setup-form-buttons">
            <Button size="large" onClick={onBack} disabled={saveTenantMutation.isLoading}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={saveTenantMutation.isLoading}
              disabled={!businessDetails.companyName || !businessDetails.timezone || !businessDetails.officePhoneNumber}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </Form>
      </div>
      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-1.png"
          alt="Landscaper at work"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "I found $83k of overhead expenses I had not been recovering in my pricing when building my first SynkedUP budget"
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Ryan Breiding</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              In The Garden Landscape
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSetupBusinessDetailsStep
